import type { operationalStatusSchema } from "@medoma/models/equipment";
import {
  allocatedEquipmentAssetsSchema,
  assetHistoryActionSchema,
  assetHistorySchema,
  equipmentAssetSchema,
  equipmentAssetsSchema,
  equipmentAssetStatusSchema,
  equipmentCategorySchema,
  equipmentCategoryWithAssetsSchema,
} from "@medoma/models/equipment";
import { z } from "zod";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import { generatePatches } from "./Helpers";
import { logisticsApi } from "./ApiClient";
import { fetchPatients, resolvePatient } from "./Patients";
import { fetchEmployee } from "./Employee";

const equipmentKeys = {
  all: ["equipment"] as const,
  asset: (assetId: string) => [...equipmentKeys.all, "asset", assetId] as const,
  lastCompletedAllocation: (assetId: string) =>
    [...equipmentKeys.asset(assetId), "lastCompletedAllocation"] as const,
  assets: () => [...equipmentKeys.all, "assets"] as const,
  categories: () => [...equipmentKeys.all, "categories"] as const,
  category: (id: string) => [...equipmentKeys.categories(), id] as const,
  careEpisodeAssets: (careEpisodeId: string) =>
    [...equipmentKeys.all, "careEpisode", careEpisodeId] as const,
};

// Legacy contract: the patients endpoint takes ids that, in the new domain,
// are care episode ids. We pass an asset's careEpisodeId where `patientIds` is expected.
const fetchPatientsByCareEpisodeIds = async ({
  careEpisodeIds,
}: {
  careEpisodeIds: string[];
}) => {
  if (careEpisodeIds.length === 0) {
    return [];
  }
  return fetchPatients({ patientIds: careEpisodeIds });
};

const enrichAssetsWithPatient = async (
  parsed: z.infer<typeof equipmentAssetsSchema>,
) => {
  const allocatedCareEpisodeIds = deduplicatePrimitiveArray(
    parsed.assets
      .filter(
        (asset) => asset.status === equipmentAssetStatusSchema.enum.allocated,
      )
      .map((asset) => asset.careEpisodeId),
  );
  const patients = await fetchPatientsByCareEpisodeIds({
    careEpisodeIds: allocatedCareEpisodeIds,
  });

  return parsed.assets.map((asset) => {
    if (asset.status === equipmentAssetStatusSchema.enum.allocated) {
      const patient = resolvePatient({
        patientId: asset.careEpisodeId,
        patients,
      });
      return { ...asset, patient };
    }
    return asset;
  });
};

const byName = <T extends { name: string }>(a: T, b: T) =>
  a.name.localeCompare(b.name);

const fetchAssets = async () => {
  const { assets, categoryMap } = await logisticsApi
    .get(`/equipment/assets`)
    .json(equipmentAssetsSchema);
  // Sort up-front so everything downstream operates on alphabetically ordered data.
  const sortedAssets = [...assets].sort(byName);
  const sortedCategories = Object.entries(categoryMap)
    .map(([id, { name }]) => ({ id, name }))
    .sort(byName);

  const enrichedAssets = await enrichAssetsWithPatient({
    assets: sortedAssets,
    categoryMap,
  });

  return sortedCategories.map(({ id, name }) => ({
    id,
    name,
    assets: enrichedAssets.filter((asset) => asset.categoryId === id),
  }));
};

export const equipmentQueryOptions = () => ({
  queryKey: equipmentKeys.assets(),
  queryFn: fetchAssets,
});

const fetchAssetsForCareEpisode = async (careEpisodeId: string) => {
  const { assets, categoryMap } = await logisticsApi
    .get(`/equipment/care-episodes/${careEpisodeId}/assets`)
    .json(allocatedEquipmentAssetsSchema);
  return assets.map((asset) => {
    const category = categoryMap[asset.categoryId];

    // Assets should always be assigned to a category
    if (!category) {
      throw new Error(
        `Asset ${asset.id} references unknown category ${asset.categoryId}`,
      );
    }
    return { ...asset, categoryName: category.name };
  });
};

export const careEpisodeAssetsQueryOptions = ({
  careEpisodeId,
}: {
  careEpisodeId: string;
}) => ({
  queryKey: equipmentKeys.careEpisodeAssets(careEpisodeId),
  queryFn: () => fetchAssetsForCareEpisode(careEpisodeId),
});

const fetchCategory = async (categoryId: string) => {
  return await logisticsApi
    .get(`/equipment/categories/${categoryId}`)
    .json(equipmentCategorySchema);
};

const fetchPatientByCareEpisodeId = async (careEpisodeId: string) => {
  const patients = await fetchPatientsByCareEpisodeIds({
    careEpisodeIds: [careEpisodeId],
  });
  return resolvePatient({ patientId: careEpisodeId, patients });
};

const fetchAsset = async (assetId: string) => {
  const parsedAsset = await logisticsApi
    .get(`/equipment/assets/${assetId}`)
    .json(equipmentAssetSchema);
  const category = await fetchCategory(parsedAsset.categoryId);

  if (parsedAsset.status === equipmentAssetStatusSchema.enum.allocated) {
    const patient = await fetchPatientByCareEpisodeId(
      parsedAsset.careEpisodeId,
    );
    return { ...parsedAsset, category, patient };
  }
  return { ...parsedAsset, category };
};

export const assetQueryOptions = ({ assetId }: { assetId: string }) => ({
  queryKey: equipmentKeys.asset(assetId),
  queryFn: () => fetchAsset(assetId),
});

const fetchAssetHistory = async (assetId: string) => {
  return await logisticsApi
    .get(`/equipment/assets/${assetId}/history`)
    .json(assetHistorySchema);
};

export const lastCompletedAllocationResultTypeSchema = z.enum([
  "found",
  "none",
  "unmatched",
]);

const findLastCompletedAllocation = (
  history: z.infer<typeof assetHistorySchema>,
) => {
  // Sort newest first so we don't depend on backend ordering when picking the most recent cycle.
  const sortedHistory = [...history].sort(
    (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime(),
  );
  const deallocated = sortedHistory.find(
    (entry) => entry.action === assetHistoryActionSchema.enum.deallocated,
  );
  if (!deallocated) {
    return { resultType: lastCompletedAllocationResultTypeSchema.enum.none };
  }
  const allocated = sortedHistory.find(
    (entry) =>
      entry.action === assetHistoryActionSchema.enum.allocated &&
      entry.careEpisodeId === deallocated.careEpisodeId &&
      entry.occurredAt <= deallocated.occurredAt,
  );
  if (!allocated) {
    return {
      resultType: lastCompletedAllocationResultTypeSchema.enum.unmatched,
    };
  }
  return {
    resultType: lastCompletedAllocationResultTypeSchema.enum.found,
    careEpisodeId: deallocated.careEpisodeId,
    allocatedAt: allocated.occurredAt,
    deallocatedAt: deallocated.occurredAt,
    returnedByEmployeeNumber: deallocated.employeeNumber,
  } as const;
};

const fetchLastCompletedAssetAllocation = async (assetId: string) => {
  const history = await fetchAssetHistory(assetId);
  const lastCompletedAllocation = findLastCompletedAllocation(history);
  if (
    lastCompletedAllocation.resultType !==
    lastCompletedAllocationResultTypeSchema.enum.found
  ) {
    return lastCompletedAllocation;
  }
  const [patient, returnedByEmployee] = await Promise.all([
    fetchPatientByCareEpisodeId(lastCompletedAllocation.careEpisodeId),
    fetchEmployee(lastCompletedAllocation.returnedByEmployeeNumber),
  ]);
  return {
    resultType: lastCompletedAllocationResultTypeSchema.enum.found,
    patient,
    allocatedAt: lastCompletedAllocation.allocatedAt,
    deallocatedAt: lastCompletedAllocation.deallocatedAt,
    returnedByEmployee,
  } as const;
};

export const lastCompletedAssetAllocationQueryOptions = ({
  assetId,
}: {
  assetId: string;
}) => ({
  queryKey: equipmentKeys.lastCompletedAllocation(assetId),
  queryFn: () => fetchLastCompletedAssetAllocation(assetId),
});

export const updateAssetComment = async ({
  assetId,
  comment,
}: {
  assetId: string;
  comment: string;
}) => {
  await logisticsApi.patch(`/equipment/assets/${assetId}`, {
    json: generatePatches({ comment }),
  });
};

export const clearAssetComment = async ({ assetId }: { assetId: string }) => {
  await logisticsApi.patch(`/equipment/assets/${assetId}`, {
    json: generatePatches({ comment: null }),
  });
};

export const deallocateAsset = async ({ assetId }: { assetId: string }) => {
  await logisticsApi.patch(`/equipment/assets/${assetId}`, {
    json: generatePatches({ careEpisode: null }),
  });
};

export const allocateAsset = async ({
  assetId,
  careEpisodeId,
}: {
  assetId: string;
  careEpisodeId: string;
}) => {
  await logisticsApi.patch(`/equipment/assets/${assetId}`, {
    json: [{ op: "add", path: "/careEpisode", value: careEpisodeId }],
  });
};

const fetchCategories = async () => {
  const parsed = await logisticsApi
    .get(`/equipment/categories`, {
      searchParams: { includeAssets: true },
    })
    .json(z.array(equipmentCategoryWithAssetsSchema));
  // Sort up-front so everything downstream operates on alphabetically ordered data.
  return parsed
    .map((category) => ({
      ...category,
      assets: [...category.assets].sort(byName),
    }))
    .sort(byName);
};

export const createCategory = ({ name }: { name: string }) =>
  logisticsApi.post(`/equipment/categories`, { json: { name } });

export const updateCategoryName = async ({
  categoryId,
  name,
}: {
  categoryId: string;
  name: string;
}) => {
  await logisticsApi.patch(`/equipment/categories/${categoryId}`, {
    json: generatePatches({ name }),
  });
};

export const deleteCategory = (categoryId: string) =>
  logisticsApi.delete(`/equipment/categories/${categoryId}`);

export const createAsset = ({
  name,
  categoryId,
}: {
  name: string;
  categoryId: string;
}) => logisticsApi.post(`/equipment/assets`, { json: { name, categoryId } });

export const updateAssetName = async ({
  assetId,
  name,
}: {
  assetId: string;
  name: string;
}) => {
  await logisticsApi.patch(`/equipment/assets/${assetId}`, {
    json: generatePatches({ name }),
  });
};

export const deleteAsset = (assetId: string) =>
  logisticsApi.delete(`/equipment/assets/${assetId}`);

export const updateAssetOperationalStatus = async ({
  assetId,
  operationalStatus,
}: {
  assetId: string;
  operationalStatus: z.infer<typeof operationalStatusSchema>;
}) => {
  await logisticsApi.patch(`/equipment/assets/${assetId}`, {
    json: generatePatches({ operationalStatus }),
  });
};

export const categoriesQueryOptions = () => ({
  queryKey: equipmentKeys.categories(),
  queryFn: fetchCategories,
});
